/**
 * Back In Stock - Klaviyo Integration
 * Handles back-in-stock notification subscriptions via Klaviyo Client API
 * API Docs: https://developers.klaviyo.com/en/reference/create_client_back_in_stock_subscription
 */

(function() {
  'use strict';

  let globalListenersBound = false;

  function getBackInStockModalFor(element) {
    return element.closest?.('back-in-stock-modal');
  }

  function handleBackInStockFormSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.classList.contains('back-in-stock-form')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const modal = getBackInStockModalFor(form);

    if (!modal || typeof modal.handleBackInStockSubmission !== 'function') {
      console.warn('[BIS] Modal not found for form submit');
      return;
    }

    modal.handleBackInStockSubmission(form);
  }

  function handleBackInStockButtonClick(event) {
    const button = event.target instanceof Element ? event.target.closest('.back-in-stock-submit') : null;

    if (!button) {
      return;
    }

    const form = button.closest('form.back-in-stock-form');

    if (!form) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const modal = getBackInStockModalFor(form);

    if (!modal || typeof modal.handleBackInStockSubmission !== 'function') {
      console.warn('[BIS] Modal not found for button click');
      return;
    }

    modal.handleBackInStockSubmission(form);
  }

  function bindGlobalListeners() {
    if (globalListenersBound) {
      return;
    }

    document.addEventListener('submit', handleBackInStockFormSubmit, true);
    document.addEventListener('click', handleBackInStockButtonClick, true);

    globalListenersBound = true;
  }

  function registerBackInStockModal(Modal) {
    if (window.customElements.get('back-in-stock-modal')) {
      bindGlobalListeners();
      return;
    }

    class BackInStockModal extends Modal {
      constructor() {
        super();

        const globalWindow = typeof window !== 'undefined' ? window : {};

        this.klaviyoPublicKey =
          this.getAttribute('public-key') ||
          globalWindow.KLAVIYO_PUBLIC_API_KEY ||
          globalWindow.klaviyoPublicApiKey ||
          'V9LaUD';

        // Store bound handler for cleanup
        this.boundFormSubmitHandler = this.handleFormSubmit.bind(this);
      }

      connectedCallback() {
        super.connectedCallback();
        bindGlobalListeners();

        // Bind form submit handler directly to the custom element
        // This ensures it works regardless of Shadow DOM boundaries
        this.addEventListener('submit', this.boundFormSubmitHandler, true);

        // Bind click handler to the submit button
        // Forms cannot be used in slotted Shadow DOM content, so we handle submission via button click
        this.addEventListener('click', (event) => {
          const button = event.target.closest('.back-in-stock-submit');

          if (!button) {
            return;
          }

          event.preventDefault();
          event.stopPropagation();

          // Get the form container (not an actual form element since those get stripped)
          const formContainer = this.querySelector('.back-in-stock-form');

          if (!formContainer) {
            console.warn('[BIS] Could not find form container');
            return;
          }

          this.handleBackInStockSubmission(formContainer);
        }, true);
      }

      disconnectedCallback() {
        // Clean up event listener
        this.removeEventListener('submit', this.boundFormSubmitHandler, true);
      }

      handleFormSubmit(event) {
        const form = event.target;

        if (!(form instanceof HTMLFormElement) || !form.classList.contains('back-in-stock-form')) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.handleBackInStockSubmission(form);
      }

      async handleBackInStockSubmission(container) {
        if (!container || container.dataset.bisSubmitting === 'true') {
          return;
        }

        container.dataset.bisSubmitting = 'true';

        const submitButton = container.querySelector('button.back-in-stock-submit');

        if (submitButton && !submitButton.dataset.originalText) {
          submitButton.dataset.originalText = submitButton.textContent.trim();
        }

        // Get email value directly from input element
        const emailInput = container.querySelector('input[name="email"]');
        const email = (emailInput?.value || '').trim();
        const variantId = this.getAttribute('data-variant-id') || '';

        if (!email) {
          this.showError(container, 'Please enter your email address.');
          delete container.dataset.bisSubmitting;
          return;
        }

        if (!this.isValidEmail(email)) {
          this.showError(container, 'Please enter a valid email address.');
          delete container.dataset.bisSubmitting;
          return;
        }

        if (!variantId) {
          this.showError(container, 'Variant unavailable. Please refresh and try again.');
          delete container.dataset.bisSubmitting;
          return;
        }

        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = 'Subscribing...';
        }

        this.hideError(container);

        try {
          await this.subscribeToKlaviyo({
            email,
            variantId
          });

          this.showSuccess(container, submitButton);
        } catch (error) {
          console.error('[BIS] Subscription failed', error);
          this.showError(container, error?.message || 'Something went wrong. Please try again.');

          if (submitButton) {
            submitButton.disabled = false;
            submitButton.textContent = submitButton.dataset.originalText || 'Notify Me';
          }
        } finally {
          delete container.dataset.bisSubmitting;
        }
      }

      showError(container, message) {
        const errorDiv = container?.querySelector('.back-in-stock-error');

        if (errorDiv) {
          errorDiv.textContent = message;
          errorDiv.style.display = 'block';
        }
      }

      hideError(container) {
        const errorDiv = container?.querySelector('.back-in-stock-error');

        if (errorDiv) {
          errorDiv.style.display = 'none';
        }
      }

      showSuccess(container, submitButton) {
        const successDiv = container?.querySelector('.back-in-stock-success');
        const innerContainer = container?.querySelector('.v-stack.gap-4');

        Array.from(innerContainer?.children || []).forEach((child) => {
          if (!(child instanceof HTMLElement)) {
            return;
          }

          if (child === successDiv) {
            child.style.display = 'flex';
          } else {
            child.style.display = 'none';
          }
        });

        setTimeout(() => {
          this.hide().finally(() => {
            setTimeout(() => {
              this.resetForm(container, submitButton, successDiv, innerContainer);
            }, 300);
          });
        }, 3000);
      }

      resetForm(container, submitButton, successDiv, innerContainer) {
        // Reset email input
        const emailInput = container?.querySelector('input[name="email"]');
        if (emailInput) emailInput.value = '';

        this.hideError(container);

        Array.from(innerContainer?.children || []).forEach((child) => {
          if (!(child instanceof HTMLElement)) {
            return;
          }

          if (child === successDiv) {
            child.style.display = 'none';
          } else {
            child.style.display = '';
          }
        });

        if (submitButton) {
          submitButton.disabled = false;
          submitButton.textContent = submitButton.dataset.originalText || 'Notify Me';
        }
      }

      async subscribeToKlaviyo({ email, variantId }) {
        if (!this.klaviyoPublicKey) {
          throw new Error('Missing Klaviyo configuration.');
        }

        const catalogVariantId = `$shopify:::$default:::${variantId}`;

        // Back-in-stock API only accepts: email, phone_number, external_id, and id
        const profileAttributes = {
          email
        };

        const payload = {
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: {
                data: {
                  type: 'profile',
                  attributes: profileAttributes
                }
              },
              channels: ['EMAIL']
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: catalogVariantId
                }
              }
            }
          }
        };

        const endpoint = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${encodeURIComponent(
          this.klaviyoPublicKey
        )}`;

        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            revision: '2024-10-15',
            Authorization: `Klaviyo-API-Key ${this.klaviyoPublicKey}`
          },
          body: JSON.stringify(payload)
        });

        if ([200, 202, 204, 409].includes(response.status)) {
          return true;
        }

        let errorMessage = 'Failed to subscribe. Please try again.';

        try {
          const errorData = await response.json();
          errorMessage = errorData.errors?.[0]?.detail || errorMessage;
        } catch (_) {
          // Ignore parse errors and use the default message.
        }

        throw new Error(errorMessage);
      }

      isValidEmail(email) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      }
    }

    window.customElements.define('back-in-stock-modal', BackInStockModal);
    bindGlobalListeners();
  }

  function initBackInStockModal() {
    const Modal = window.customElements?.get('x-modal');

    if (Modal) {
      registerBackInStockModal(Modal);
      return;
    }

    if (window.customElements?.whenDefined) {
      window.customElements
        .whenDefined('x-modal')
        .then(() => {
          const ModalClass = window.customElements.get('x-modal');

          if (ModalClass) {
            registerBackInStockModal(ModalClass);
          }
        })
        .catch(() => {
          // Swallow errors so they do not break the storefront.
        });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBackInStockModal);
  } else {
    initBackInStockModal();
  }
})();
